import React, { useCallback, useEffect, useMemo, useState } from "react";
import styled from "styled-components";
import { Form, Row, Col } from "react-bootstrap";

import { hexToRGBA } from "../../../utils/theme";
import { usePrevious } from "../../../hooks";

import {
  state_array as STATES,
  setItemSessionStorage,
  getItemSessionStorage,
  KEY_PRE_INC_ORDER_INFO,
  KEY_PRE_INC_ORDER_INFO_BACKUP,
  KEY_INC_SUMMARY,
  KEY_INC_SUMMARY_BACKUP,
} from "../../../../variables/Variables";

import StyledButton from "../../styled/StyledButton";
import router from "next/router";

function formatPhoneNumber(phoneNumber) {
  return `(${phoneNumber?.slice(0, 3)}) ${phoneNumber?.slice(3, 6)}-${phoneNumber?.slice(6)}`;
}

function getSummaryProductTitle(code, customName, state) {
  if (code === "EIN_LLCIncorporation") {
    return "Our Processing Fee";
  }

  if (code === "EIN_ResidentAgent") {
    return `${customName} (Annually)`;
  }

  if (code === "EIN_StateIncorporationFee") {
    return `${customName} (Charged ${STATES[state]})`;
  }

  return customName;
}

const StyledLLCIncFinalizeForm = styled(Form)`
  .block-divider {
    margin-bottom: 48px;
  }

  .primary-title {
    margin-bottom: 16px;
    padding-left: 24px;
    padding-right: 24px;

    font-family: "MerriweatherLight";
    font-size: 24px;
    line-height: 30px;
    text-align: center;
    text-transform: capitalize;
    color: #1b3858;
    text-shadow: 1px 1px 0px #ffffff;

    span {
      span {
        color: #c61f0c;
      }
    }
  }

  .secondary-title {
    margin-top: 16px;
    margin-bottom: 16px;
    padding-left: 24px;
    padding-right: 24px;

    font-family: "MerriweatherLight";
    font-size: 18px;
    line-height: 28px;
    text-align: center;
    color: #1b3858;
    text-shadow: 1px 1px 0px #ffffff;
  }

  .info-card {
    margin-bottom: 30px;
    padding-top: 40px;
    padding-bottom: 40px;
    padding-left: 25px;
    padding-right: 25px;
    border: 2px solid ${hexToRGBA("#1B3858", 0.2)};
    border-radius: 7px;

    .info-card-label {
      font-family: "LatoRegular";
      font-size: 16px;
      line-height: 24px;
      color: #1b3858;
      text-shadow: 1px 1px 0px #ffffff;
      text-align: center;
    }

    .info-card-field {
      padding-top: 12px;
      padding-bottom: 12px;

      font-family: "LatoRegular";
      font-size: 14px;
      line-height: 24px;
      color: #1b3858;
      text-shadow: 1px 1px 0px #ffffff;

      &:nth-child(2n) {
        background: #f5f5f5;
      }
    }

    .info-card-link {
      width: 100%;
      font-family: "LatoBold";
      font-size: 14px;
      line-height: 24px;
      color: #078ff1;
      text-shadow: 1px 1px 0px #ffffff;
      text-align: center;

      &:hover {
        font-family: "LatoRegular";
        text-decoration: underline;
        cursor: pointer;
      }
    }
  }

  button {
    width: 100%;
    margin-bottom: 1rem;
  }

  @media ${({ theme }) => theme.devices.tablet} {
    .primary-title {
      font-size: 20px;
      line-height: 26px;
    }

    .info-card {
      padding-left: 16px;
      padding-right: 16px;
    }

    .block-divider {
      margin-bottom: 24px;
    }
  }
`;

function LLCIncFinalizeForm({ values = {}, onSubmit }) {
  const [state, setCommonInfo] = useState(() => {
    const initState = {
      residentAgent: "true",
      companyName: "",
      altCompanyName: "",
      firstName: "",
      middleName: "",
      lastName: "",
      email: "",
      phone: "",
      corpAddressAddress1: "",
      corpAddressAddress2: "",
      corpAddressCity: "",
      corpAddressZip: "",
      corpAddressState: "",
    };

    const cashedPreOrder = getItemSessionStorage(KEY_PRE_INC_ORDER_INFO);

    if (cashedPreOrder && typeof cashedPreOrder === "object" && Object.keys(cashedPreOrder).length > 0) {
      if (cashedPreOrder.products && cashedPreOrder.products.length >= 1) {
        const cashedMainProduct = cashedPreOrder?.products?.find(
          (product) => product.code.main && product.processingOption !== "UPSELL"
        );

        initState.companyName = cashedMainProduct?.dbaname;
        initState.altCompanyName = cashedPreOrder.altName;
        initState.firstName = cashedMainProduct.owner?.firstName;
        initState.middleName = cashedMainProduct.owner?.middleName;
        initState.lastName = cashedMainProduct.owner?.lastName;
        initState.corpAddressAddress1 = cashedMainProduct?.corpAddress?.address1;
        initState.corpAddressAddress2 = cashedMainProduct?.corpAddress?.address2;
        initState.corpAddressCity = cashedMainProduct?.corpAddress?.city;
        initState.corpAddressZip = cashedMainProduct?.corpAddress?.zip;
        initState.corpAddressState = cashedMainProduct?.corpAddress?.state;
        initState.phone = cashedPreOrder.customer.phone;
        initState.email = cashedPreOrder.customer.email;
        initState.residentAgent = cashedPreOrder.residentAgent;
      }
    }

    return initState;
  });

  const prevState = usePrevious(state);
  const isRAEnabled = state.residentAgent === "true";
  const incState = useMemo(() => values.summary.find(({ state }) => state)?.state, [values]);

  const handleOrderEdit = useCallback(() => {
    router.push("/LLCInc");
  }, [router]);

  const handleRAEdit = () => {
    setCommonInfo((prevState) => {
      return {
        ...prevState,
        residentAgent: prevState.residentAgent === "true" ? "false" : "true",
      };
    });
  };

  useEffect(() => {
    if (prevState && prevState.residentAgent !== state.residentAgent) {
      const cashedPreOrder = getItemSessionStorage(KEY_PRE_INC_ORDER_INFO);

      if (cashedPreOrder) {
        setItemSessionStorage(KEY_PRE_INC_ORDER_INFO, {
          ...cashedPreOrder,
          residentAgent: state.residentAgent,
        });
      }

      router.push("/LLCInc");
    }
  }, [router, prevState, state]);

  useEffect(() => {
    const cashedPreOrder = getItemSessionStorage(KEY_PRE_INC_ORDER_INFO);
    const cashedIncSummary = getItemSessionStorage(KEY_INC_SUMMARY);

    if (cashedPreOrder) {
      setItemSessionStorage(KEY_PRE_INC_ORDER_INFO_BACKUP, cashedPreOrder);
    }

    if (cashedIncSummary) {
      setItemSessionStorage(KEY_INC_SUMMARY_BACKUP, cashedIncSummary);
    }
  }, []);

  return (
    <StyledLLCIncFinalizeForm id="kform" noValidate validated={values.validated} onSubmit={onSubmit}>
      <Row>
        <Col xs={12}>
          <div className="block-divider" />
        </Col>
        <Col xs={12}>
          <div className="primary-title">
            <span>
              Your LLC Registration is Almost Complete
              <br />
              Review & Finalize Your Order Below
            </span>
          </div>
        </Col>
        <Col xs={12}>
          <div className="block-divider" />
        </Col>
      </Row>
      <Row>
        <Col xs={12} md={7}>
          <Col xs={12} className="info-card">
            <Col xs={12}>
              <div className="primary-title">
                <span>LLC Summary</span>
              </div>
            </Col>
            <Col xs={12}>
              <div className="info-card-link" onClick={handleOrderEdit}>
                Double Check Your Information is Correct
                <br />
                Something incorrect? Click here to edit
              </div>
            </Col>
            <Col xs={12}>
              <div className="block-divider" />
            </Col>
            <Col xs={12}>
              <div className="secondary-title">
                <span>Legal Information</span>
              </div>
            </Col>
            <Col xs={12} className="info-card-field">
              <Row>
                <Col xs={6}>
                  <span>Limited Liability Company Name</span>
                </Col>
                <Col xs={6}>
                  <strong>{state.companyName}</strong>
                </Col>
              </Row>
            </Col>
            <Col xs={12} className="info-card-field">
              <Row>
                <Col xs={6}>
                  <span>Alternate Company Name</span>
                </Col>
                <Col xs={6}>
                  <strong>{state.altCompanyName}</strong>
                </Col>
              </Row>
            </Col>
            <Col xs={12} className="info-card-field">
              <Row>
                <Col xs={6}>
                  <span>Incorporate in</span>
                </Col>
                <Col xs={6}>
                  <strong>{STATES[incState]}</strong>
                </Col>
              </Row>
            </Col>
            <Col xs={12}>
              <div className="block-divider" />
            </Col>
            <Col xs={12}>
              <div className="secondary-title">
                <span>Primary Officer</span>
              </div>
            </Col>
            <Col xs={12} className="info-card-field">
              <Row>
                <Col xs={6}>
                  <span>First Name</span>
                </Col>
                <Col xs={6}>
                  <strong>{state.firstName}</strong>
                </Col>
              </Row>
            </Col>
            <Col xs={12} className="info-card-field">
              <Row>
                <Col xs={6}>
                  <span>Middle Name</span>
                </Col>
                <Col xs={6}>
                  <strong>{state.middleName}</strong>
                </Col>
              </Row>
            </Col>
            <Col xs={12} className="info-card-field">
              <Row>
                <Col xs={6}>
                  <span>Last Name</span>
                </Col>
                <Col xs={6}>
                  <strong>{state.lastName}</strong>
                </Col>
              </Row>
            </Col>
            <Col xs={12} className="info-card-field">
              <Row>
                <Col xs={6}>
                  <span>Phone Number</span>
                </Col>
                <Col xs={6}>
                  <strong>{formatPhoneNumber(state.phone)}</strong>
                </Col>
              </Row>
            </Col>
            <Col xs={12} className="info-card-field">
              <Row>
                <Col xs={6}>
                  <span>Email Address</span>
                </Col>
                <Col xs={6}>
                  <strong>{state.email}</strong>
                </Col>
              </Row>
            </Col>
            <Col xs={12}>
              <div className="block-divider" />
            </Col>
            <Col xs={12}>
              <div className="secondary-title">
                <span>
                  Corporate Address
                  <br />
                  (All Legal Correspondence Will Be Received Here)
                </span>
              </div>
            </Col>

            {isRAEnabled && (
              <>
                <Col xs={12}>
                  <div className="info-card-label">
                    <strong>You have pre-ordered using Resident Agent service.</strong>
                  </div>
                </Col>
                <Col xs={12}>
                  <div className="info-card-label">
                    <strong>Do you want to preserve this?</strong>
                  </div>
                </Col>
                <Col xs={12}>
                  <div className="secondary-title" />
                </Col>
                <Col xs={12}>
                  <div className="info-card-link" onClick={handleRAEdit}>
                    No, I Will Use My Home Address
                  </div>
                </Col>
              </>
            )}

            {!isRAEnabled && (
              <>
                <Col xs={12}>
                  <div className="info-card-label">
                    <strong>Do you wish to use our Resident Agent service?</strong>
                  </div>
                </Col>
                <Col xs={12}>
                  <div className="info-card-link" onClick={handleRAEdit}>
                    Yes, Use Resident Agent
                  </div>
                </Col>
                <Col xs={12}>
                  <div className="secondary-title" />
                </Col>
                <Col xs={12} className="info-card-field">
                  <Row>
                    <Col xs={6}>
                      <span>Address</span>
                    </Col>
                    <Col xs={6}>
                      <strong>{state.corpAddressAddress1}</strong>
                    </Col>
                  </Row>
                </Col>
                <Col xs={12} className="info-card-field">
                  <Row>
                    <Col xs={6}>
                      <span>Address 2</span>
                    </Col>
                    <Col xs={6}>
                      <strong>{state.corpAddressAddress2}</strong>
                    </Col>
                  </Row>
                </Col>
                <Col xs={12} className="info-card-field">
                  <Row>
                    <Col xs={6}>
                      <span>City</span>
                    </Col>
                    <Col xs={6}>
                      <strong>{state.corpAddressCity}</strong>
                    </Col>
                  </Row>
                </Col>
                <Col xs={12} className="info-card-field">
                  <Row>
                    <Col xs={6}>
                      <span>ZIP / Postal Code</span>
                    </Col>
                    <Col xs={6}>
                      <strong>{state.corpAddressZip}</strong>
                    </Col>
                  </Row>
                </Col>
                <Col xs={12} className="info-card-field">
                  <Row>
                    <Col xs={6}>
                      <span>State / Province</span>
                    </Col>
                    <Col xs={6}>
                      <strong>{STATES[state.corpAddressState]}</strong>
                    </Col>
                  </Row>
                </Col>
              </>
            )}
          </Col>
        </Col>
        <Col xs={12} md={5}>
          <Col xs={12} className="info-card">
            <Col xs={12}>
              <div className="primary-title">
                <span>Order Summary</span>
              </div>
            </Col>
            <Col xs={12}>
              <div className="secondary-title" />
            </Col>

            {values.summary
              .filter(({ state }) => state !== "ME")
              .map(({ code, customerName, price, state }) => (
                <Col xs={12} key={code} className="info-card-field">
                  <Row>
                    <Col xs={6}>
                      <span>{getSummaryProductTitle(code, customerName, state)}</span>
                    </Col>
                    <Col xs={6}>
                      <strong>${price}.00</strong>
                    </Col>
                  </Row>
                </Col>
              ))}
          </Col>
          <Row>
            <Col xs={0} md={3} />
            <Col xs={12} md={6}>
              <StyledButton type="submit">Submit Application</StyledButton>
            </Col>
            <Col xs={0} md={3} />
          </Row>
        </Col>
      </Row>
    </StyledLLCIncFinalizeForm>
  );
}

export default LLCIncFinalizeForm;
